import { useToken } from "@gluestack-style/react";
import { Box, Button, HStack } from "components/ui";
import { Text } from "components/ui";
import { ChevronLeft, ChevronRight } from "lucide-react-native";
import React from "react";
import { useIsMobile } from "utils/hooks/useIsMobile";

type PaginationProps = {
	pageCount?: number;
	pageNumber?: number;
	onChangePage: (pageNumber: number) => void;
	variant?: "default" | "bullet";
	scrollRef?: React.RefObject<any>;
};

const PaginationButton = ({ page, isSelected, onPress, variant }: { page: number | string; isSelected: boolean; onPress: () => void; variant?: "default" | "bullet" }) => {
	const [isHovered, setIsHovered] = React.useState(false);
	const resolvedPrimarylight = useToken("colors", "primarylight");
	if (variant === "bullet") {
		return (
			<Box h={"$full"} justifyContent="center">
				<Box borderRadius={"$full"} borderWidth={2} backgroundColor={isSelected ? "$darkteal" : isHovered ? "#E1E9F0" : "$basicwhite"} w={12} h={12} />
			</Box>
		);
	}
	return (
		<Button
			onPress={onPress}
			onHoverIn={() => setIsHovered(true)}
			onHoverOut={() => setIsHovered(false)}
			borderRadius={4}
			borderWidth={1}
			borderColor={isSelected ? "$primarylight" : "#E1E9F0"}
			backgroundColor={isSelected ? "$primarylight" : isHovered ? "#E1E9F0" : "$basicwhite"}
			// w={28}
			// h={28}
			minWidth={28}
			minHeight={28}
			padding={0}
			justifyContent="center"
			alignItems="center"
			sx={{
				paddingVertical: 0,
				paddingHorizontal: 0,
			}}
		>
			<Text fontFamily="inter" color={isSelected ? "$basicwhite" : "$primarylight"} fontWeight={isSelected ? "bold" : "normal"} paddingHorizontal={4} fontSize={16}>
				{page}
			</Text>
		</Button>
	);
};

export default function Pagination({ pageCount, pageNumber, onChangePage, variant = "default", scrollRef }: PaginationProps) {
	const resolvedPrimarylight = useToken("colors", "primarylight");
	const resolvedDarkteal = useToken("colors", "darkteal");
	const handleChangePage = (newPage: number) => {
		onChangePage(newPage);

		// Scroll to top when page changes
		if (scrollRef?.current) {
			scrollRef.current.scrollTo({ y: 0, animated: true });
		}
	};
	const isMobile = useIsMobile();

	if (pageCount == null || pageNumber == null || pageCount === 1) {
		return null;
	}

	const isFirstPage = pageNumber === 1;
	const isLastPage = pageNumber === pageCount;

	const requiredPages: (number | string)[] = [];
	if (pageCount <= 10) {
		for (let i = 1; i <= pageCount; i++) {
			requiredPages.push(i);
		}
	} else {
		if (!isMobile) {
			if (pageNumber < 5) {
				requiredPages.push(1, 2, 3, 4, 5, 6, 7, "...", pageCount - 1, pageCount);
			} else if (pageNumber > pageCount - 5) {
				requiredPages.push(1, 2, "...", pageCount - 6, pageCount - 5, pageCount - 4, pageCount - 3, pageCount - 2, pageCount - 1, pageCount);
			} else {
				requiredPages.push(1, 2, "...", pageNumber - 2, pageNumber - 1, pageNumber, pageNumber + 1, pageNumber + 2, "...", pageCount - 1, pageCount);
			}
		} else {
			if (pageNumber < 3) {
				requiredPages.push(1, 2, 3, "...", pageCount - 1, pageCount);
			} else if (pageNumber > pageCount - 3) {
				requiredPages.push(1, 2, "...", pageCount - 2, pageCount - 1, pageCount);
			} else {
				requiredPages.push(1, 2, "...", pageNumber, "...", pageCount - 1, pageCount);
			}
		}
	}

	return (
		<HStack role="navigation" gap={variant === "bullet" ? 20 : 8}>
			{!isFirstPage && (
				<Button
					padding={0}
					borderRadius={6}
					borderWidth={1}
					borderColor={variant === "bullet" ? "$darkteal" : "#E1E9F0"}
					w={variant === "bullet" ? 40 : 28}
					h={variant === "bullet" ? 40 : 28}
					onPress={() => handleChangePage(pageNumber - 1)}
					$hover-bgColor={"#E1E9F0"}
				>
					<ChevronLeft color={variant === "bullet" ? resolvedDarkteal : resolvedPrimarylight} />
				</Button>
			)}
			<HStack gap={variant === "bullet" ? 20 : 8}>
				{requiredPages.map((page, i) => {
					return <PaginationButton variant={variant} key={i} page={page} isSelected={page === pageNumber} onPress={() => handleChangePage(page as number)} />;
				})}
			</HStack>
			{!isLastPage && pageCount > 1 && (
				<Button
					padding={0}
					borderRadius={6}
					borderWidth={1}
					borderColor={variant === "bullet" ? "$darkteal" : "#E1E9F0"}
					w={variant === "bullet" ? 40 : 28}
					h={variant === "bullet" ? 40 : 28}
					onPress={() => handleChangePage(pageNumber + 1)}
					$hover-bgColor={"#E1E9F0"}
				>
					<ChevronRight color={variant === "bullet" ? resolvedDarkteal : resolvedPrimarylight} />
				</Button>
			)}
		</HStack>
	);
}
